import React, { useEffect, useState } from "react";
import api from "../../api";
import { useNavigate } from "react-router-dom";

const Posts = () => {
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [searchInput, setSearchInput] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [error, setError] = useState("");
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);

  const pageSize = 9;

  const getImageUrl = (path) => {
    if (!path) return null;
    if (path.startsWith("http://") || path.startsWith("https://")) return path;
    return path.startsWith("/") ? `${process.env.REACT_APP_IMG_URL}${path}` : `${process.env.REACT_APP_IMG_URL}/${path}`;
  };

  const fetchPosts = async (pageToLoad = 1, append = false, search = "") => {
    try {
      append ? setLoadingMore(true) : setLoading(true);
      setError("");

      const response = await api.get("/posts", { params: { page: pageToLoad, pageSize } });

      let newPosts = Array.isArray(response.data) ? response.data : [];

      if (search) {
        const loweredSearch = search.toLowerCase();
        newPosts = newPosts.filter((post) => {
          const categories = Array.isArray(post.categories) ? post.categories.join(" ").toLowerCase() : "";
          return (
            post.title?.toLowerCase().includes(loweredSearch) ||
            post.description?.toLowerCase().includes(loweredSearch) ||
            post.slug?.toLowerCase().includes(loweredSearch) ||
            categories.includes(loweredSearch)
          );
        });
      }

      setPosts((prev) => (append ? [...prev, ...newPosts] : newPosts));
      setPage(pageToLoad);
      setHasMore(newPosts.length === pageSize);
    } catch (err) {
      console.error(err);
      setError("Erreur lors du chargement des posts.");
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  useEffect(() => { fetchPosts(1, false, searchTerm); }, [searchTerm]);

  const handleSubmit = (e) => { e.preventDefault(); setSearchTerm(searchInput.trim()); };
  const handleResetSearch = () => { setSearchInput(""); setSearchTerm(""); };
  const handleLoadMore = () => { if (!hasMore || loadingMore) return; fetchPosts(page + 1, true, searchTerm); };
  const handleEdit = (slug) => navigate(`/dashboard/posts/edit/${slug}`);

  const handleDelete = async (id) => {
    if (!window.confirm("Es-tu sûr de vouloir supprimer ce post ?")) return;
    setDeletingId(id);
    setError("");
    try {
      await api.delete(`/admin/posts/${id}`);
      setPosts((prev) => prev.filter((post) => post.id !== id));
    } catch (err) {
      console.error("Erreur lors de la suppression", err);
      setError(err.response?.data?.message || err.response?.data?.title || "Erreur lors de la suppression du post.");
    } finally {
      setDeletingId(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 text-slate-100">
        <div className="container mx-auto px-4 py-6">
          <div className="rounded-2xl border border-slate-800 bg-slate-900 p-6 shadow-lg">
            <p className="text-slate-300">Chargement en cours...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100">
      <main className="container mx-auto px-4 py-6">
        <section className="mb-8 overflow-hidden rounded-3xl bg-gradient-to-r from-slate-900 via-slate-800 to-blue-900 text-white shadow-sm">
          <div className="p-8 md:p-10">
            <p className="mb-3 inline-flex rounded-full bg-white/10 px-3 py-1 text-sm font-medium text-blue-100">
              Dashboard posts
            </p>
            <h1 className="mb-3 text-3xl font-bold md:text-4xl">Gestion des posts</h1>
            <p className="max-w-2xl text-sm leading-7 text-slate-200 md:text-base">
              Gérez vos articles, recherchez du contenu et modifiez vos publications.
            </p>
          </div>
        </section>

        <section className="rounded-2xl border border-slate-800 bg-slate-900 p-4 shadow-lg">
          <div className="mb-6 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <form onSubmit={handleSubmit} className="flex flex-col gap-3 md:flex-row">
              <input
                type="text" placeholder="Rechercher par titre, description, slug ou catégorie..."
                value={searchInput} onChange={(e) => setSearchInput(e.target.value)}
                className="w-full rounded-xl border border-slate-700 bg-slate-950 px-4 py-2.5 text-slate-100 placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500 md:w-96"
              />
              <button type="submit" className="rounded-xl bg-blue-500 px-4 py-2.5 font-semibold text-white transition hover:bg-blue-600">Rechercher</button>
              <button type="button" onClick={handleResetSearch} className="rounded-xl bg-slate-700 px-4 py-2.5 font-semibold text-white transition hover:bg-slate-600">Réinitialiser</button>
            </form>
            <button onClick={() => navigate("/dashboard/posts/create")}
              className="rounded-xl bg-green-500 px-4 py-2.5 font-semibold text-white shadow-md transition hover:bg-green-600">
              Créer un post
            </button>
          </div>

          {searchTerm && (
            <p className="mb-4 text-sm text-slate-400">
              Résultats pour <span className="font-semibold text-slate-200">"{searchTerm}"</span>
            </p>
          )}

          {error && (
            <div className="mb-4 rounded-xl border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-300">{error}</div>
          )}

          <div className="overflow-x-auto rounded-xl border border-slate-800">
            <table className="min-w-full bg-slate-900">
              <thead>
                <tr className="bg-slate-800 text-slate-200">
                  <th className="border border-slate-700 px-4 py-3 text-left font-semibold">Titre</th>
                  <th className="border border-slate-700 px-4 py-3 text-left font-semibold">Image</th>
                  <th className="border border-slate-700 px-4 py-3 text-left font-semibold">Catégories</th>
                  <th className="border border-slate-700 px-4 py-3 text-left font-semibold">Description</th>
                  <th className="border border-slate-700 px-4 py-3 text-left font-semibold">Date</th>
                  <th className="border border-slate-700 px-4 py-3 text-left font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody>
                {posts.length > 0 ? (
                  posts.map((post) => {
                    const imageUrl = getImageUrl(post.thumbnail || post.image);
                    return (
                      <tr key={post.id} className="transition hover:bg-slate-800/60">
                        <td className="border border-slate-800 px-4 py-3 align-top text-slate-200">
                          <p className="font-semibold text-white">{post.title}</p>
                          <p className="mt-1 text-xs text-slate-500">{post.slug}</p>
                        </td>
                        <td className="border border-slate-800 px-4 py-3 align-top">
                          {imageUrl ? (
                            <img className="h-20 w-28 rounded-lg border border-slate-700 object-cover" src={imageUrl} alt={post.title} loading="lazy" decoding="async" />
                          ) : (
                            <span className="text-slate-500">Aucune image</span>
                          )}
                        </td>
                        <td className="border border-slate-800 px-4 py-3 align-top">
                          {post.categories?.length > 0 ? (
                            <div className="flex flex-wrap gap-2">
                              {post.categories.map((category) => (
                                <span key={category} className="inline-flex rounded-full bg-blue-500/10 px-3 py-1 text-xs font-semibold text-blue-300">{category}</span>
                              ))}
                            </div>
                          ) : (
                            <span className="text-slate-500">Aucune catégorie</span>
                          )}
                        </td>
                        <td className="border border-slate-800 px-4 py-3 align-top">
                          <p className="line-clamp-3 max-w-xl text-left text-slate-300">{post.description}</p>
                        </td>
                        <td className="border border-slate-800 px-4 py-3 align-top text-sm text-slate-400">
                          {post.createdAt ? new Date(post.createdAt).toLocaleDateString("fr-BE") : "—"}
                        </td>
                        <td className="border border-slate-800 px-4 py-3 align-top">
                          <div className="flex flex-wrap gap-2">
                            <button onClick={() => handleEdit(post.slug)}
                              className="rounded-lg bg-blue-500 px-3 py-1.5 text-sm font-semibold text-white transition hover:bg-blue-600">Modifier</button>
                            <button onClick={() => handleDelete(post.id)} disabled={deletingId === post.id}
                              className="rounded-lg bg-red-500 px-3 py-1.5 text-sm font-semibold text-white transition hover:bg-red-600 disabled:cursor-not-allowed disabled:opacity-60">
                              {deletingId === post.id ? "Suppression..." : "Supprimer"}
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan="6" className="border border-slate-800 px-4 py-6 text-center text-slate-400">Aucun post trouvé.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </section>

        {hasMore && (
          <div className="mt-8 flex justify-center">
            <button onClick={handleLoadMore} disabled={loadingMore}
              className="rounded-xl bg-orange-500 px-6 py-3 font-semibold text-white transition hover:bg-orange-600 disabled:cursor-not-allowed disabled:bg-orange-300">
              {loadingMore ? "Chargement..." : "Charger plus"}
            </button>
          </div>
        )}
      </main>
    </div>
  );
};

export default Posts;